// Visitor Tracking Utility
// Tracks unique visitors using Firestore (with localStorage fallback)

import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  increment, 
  serverTimestamp,
  getDocs,
  query,
  where,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Check if Firebase is configured
const isFirebaseConfigured = () => {
  try {
    return db && process.env.REACT_APP_FIREBASE_PROJECT_ID !== 'YOUR_PROJECT_ID';
  } catch (error) {
    return false;
  }
};

// Firestore collection names
const STATS_COLLECTION = 'visitorStats';
const VISITS_COLLECTION = 'visits';
const STATS_DOC_ID = 'main';

// Track visitor using Firestore
const trackVisitorFirestore = async () => {
  try {
    const today = new Date().toISOString().split('T')[0];
    const visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Check if this session already tracked
    const sessionVisitors = JSON.parse(sessionStorage.getItem('portfolio-session-visitors') || '[]');
    const isNewVisitor = !sessionVisitors.includes(visitorId);
    
    if (!isNewVisitor) {
      // Already tracked in this session
      return await getVisitorStatsFirestore();
    }
    
    // Add to session
    sessionVisitors.push(visitorId);
    sessionStorage.setItem('portfolio-session-visitors', JSON.stringify(sessionVisitors));
    
    // Get or create stats document
    const statsRef = doc(db, STATS_COLLECTION, STATS_DOC_ID);
    const statsSnap = await getDoc(statsRef);
    
    if (!statsSnap.exists()) {
      // Initialize stats
      await setDoc(statsRef, {
        totalVisits: 0,
        uniqueVisitors: 0,
        lastVisit: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    
    // Increment total visits
    await updateDoc(statsRef, {
      totalVisits: increment(1),
      lastVisit: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Track daily visit
    const visitRef = doc(db, VISITS_COLLECTION, `${today}_${Date.now()}`);
    await setDoc(visitRef, {
      date: today,
      timestamp: serverTimestamp(),
      visitorId: visitorId
    });
    
    // Check if this is a unique visitor (simple check: if last visit was > 24h ago)
    const currentStats = await getDoc(statsRef);
    const lastVisit = currentStats.data()?.lastVisit?.toDate();
    const now = new Date();
    
    if (!lastVisit || (now - lastVisit) > 24 * 60 * 60 * 1000) {
      await updateDoc(statsRef, {
        uniqueVisitors: increment(1),
        updatedAt: serverTimestamp()
      });
    }
    
    return await getVisitorStatsFirestore();
  } catch (error) {
    console.error('Error tracking visitor in Firestore:', error);
    // Fallback to localStorage
    return trackVisitorLocalStorage();
  }
};

// Get visitor stats from Firestore
const getVisitorStatsFirestore = async () => {
  try {
    const statsRef = doc(db, STATS_COLLECTION, STATS_DOC_ID);
    const statsSnap = await getDoc(statsRef);
    
    if (!statsSnap.exists()) {
      return {
        totalVisits: 0,
        uniqueVisitors: 0,
        todayVisits: 0,
        thisWeekVisits: 0,
        thisMonthVisits: 0,
        lastVisit: null
      };
    }
    
    const stats = statsSnap.data();
    const today = new Date().toISOString().split('T')[0];
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    
    // Get daily visits from visits collection
    const visitsQuery = query(
      collection(db, VISITS_COLLECTION),
      where('date', '>=', monthAgo.toISOString().split('T')[0]),
      orderBy('date', 'desc')
    );
    
    const visitsSnap = await getDocs(visitsQuery);
    const visits = visitsSnap.docs.map(doc => doc.data());
    
    const todayVisits = visits.filter(v => v.date === today).length;
    const thisWeekVisits = visits.filter(v => new Date(v.date) >= weekAgo).length;
    const thisMonthVisits = visits.length;
    
    return {
      totalVisits: stats.totalVisits || 0,
      uniqueVisitors: stats.uniqueVisitors || 0,
      todayVisits,
      thisWeekVisits,
      thisMonthVisits,
      lastVisit: stats.lastVisit?.toDate()?.toISOString() || null
    };
  } catch (error) {
    console.error('Error getting visitor stats from Firestore:', error);
    // Fallback to localStorage
    return getVisitorStatsLocalStorage();
  }
};

// Reset visitor stats in Firestore
const resetVisitorStatsFirestore = async () => {
  try {
    const statsRef = doc(db, STATS_COLLECTION, STATS_DOC_ID);
    await setDoc(statsRef, {
      totalVisits: 0,
      uniqueVisitors: 0,
      lastVisit: null,
      updatedAt: serverTimestamp()
    });
    
    // Delete all visits (optional - you might want to keep historical data)
    const visitsQuery = query(collection(db, VISITS_COLLECTION));
    const visitsSnap = await getDocs(visitsQuery);
    const batch = [];
    visitsSnap.docs.forEach(doc => {
      batch.push(doc.ref.delete());
    });
    await Promise.all(batch);
    
    // Also clear session storage
    sessionStorage.removeItem('portfolio-session-visitors');
  } catch (error) {
    console.error('Error resetting visitor stats in Firestore:', error);
    resetVisitorStatsLocalStorage();
  }
};

// LocalStorage fallback functions
const trackVisitorLocalStorage = () => {
  const today = new Date().toISOString().split('T')[0];
  const visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const stats = JSON.parse(localStorage.getItem('portfolio-visitor-stats') || '{}');
  
  if (!stats.totalVisits) {
    stats.totalVisits = 0;
    stats.uniqueVisitorCount = 0;
    stats.dailyVisits = {};
    stats.lastVisit = null;
  }
  
  const sessionVisitors = JSON.parse(sessionStorage.getItem('portfolio-session-visitors') || '[]');
  const isNewVisitor = !sessionVisitors.includes(visitorId);
  
  if (isNewVisitor) {
    sessionVisitors.push(visitorId);
    sessionStorage.setItem('portfolio-session-visitors', JSON.stringify(sessionVisitors));
    
    stats.totalVisits = (stats.totalVisits || 0) + 1;
    
    if (!stats.dailyVisits[today]) {
      stats.dailyVisits[today] = 0;
    }
    stats.dailyVisits[today] += 1;
    
    stats.lastVisit = new Date().toISOString();
    
    if (!stats.lastUniqueVisit || 
        (new Date() - new Date(stats.lastUniqueVisit)) > 24 * 60 * 60 * 1000) {
      stats.uniqueVisitorCount = (stats.uniqueVisitorCount || 0) + 1;
      stats.lastUniqueVisit = new Date().toISOString();
    }
    
    localStorage.setItem('portfolio-visitor-stats', JSON.stringify(stats));
  }
  
  return stats;
};

const getVisitorStatsLocalStorage = () => {
  const stats = JSON.parse(localStorage.getItem('portfolio-visitor-stats') || '{}');
  const today = new Date().toISOString().split('T')[0];
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  const monthAgo = new Date();
  monthAgo.setMonth(monthAgo.getMonth() - 1);
  
  const todayVisits = stats.dailyVisits?.[today] || 0;
  const thisWeekVisits = Object.entries(stats.dailyVisits || {})
    .filter(([date]) => new Date(date) >= weekAgo)
    .reduce((sum, [, count]) => sum + count, 0);
  const thisMonthVisits = Object.entries(stats.dailyVisits || {})
    .filter(([date]) => new Date(date) >= monthAgo)
    .reduce((sum, [, count]) => sum + count, 0);
  
  return {
    totalVisits: stats.totalVisits || 0,
    uniqueVisitors: stats.uniqueVisitorCount || 0,
    todayVisits,
    thisWeekVisits,
    thisMonthVisits,
    lastVisit: stats.lastVisit || null,
  };
};

const resetVisitorStatsLocalStorage = () => {
  localStorage.removeItem('portfolio-visitor-stats');
  sessionStorage.removeItem('portfolio-session-visitors');
};

// Main exported functions (use Firestore if configured, otherwise localStorage)
export const trackVisitor = async () => {
  if (isFirebaseConfigured()) {
    return await trackVisitorFirestore();
  } else {
    return trackVisitorLocalStorage();
  }
};

export const getVisitorStats = async () => {
  if (isFirebaseConfigured()) {
    return await getVisitorStatsFirestore();
  } else {
    return getVisitorStatsLocalStorage();
  }
};

export const resetVisitorStats = async () => {
  if (isFirebaseConfigured()) {
    await resetVisitorStatsFirestore();
  } else {
    resetVisitorStatsLocalStorage();
  }
};
